import React, { useState } from "react";
import styled from "styled-components";

export interface ProductCategoryCardProps {
  title: string;
  badgeText?: string;
  icon?: string;
  image?: string;
  onClick?: () => void;
  selected?: boolean;
}

interface StyledCardProps {
  isActive: boolean;
}

const CardContainer = styled.div<StyledCardProps>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  isolation: isolate;
  width: 100%;
  min-height: 296.71px;
  height: auto;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  flex: none;
  background: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${(props) =>
    props.isActive ? "0px 8px 16px rgba(67, 93, 255, 0.25)" : "none"};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 8px 16px rgba(67, 93, 255, 0.2);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 8px;
    pointer-events: none;
    z-index: 10;
    transition: box-shadow 0.2s ease;
    box-shadow: ${(props) =>
      props.isActive ? "inset 0 0 0 1px #435DFF" : "inset 0 0 0 1px #D9DBDE"};
  }

  @media (max-width: 440px) {
    min-height: auto;
    width: 100px;
    height: 180px;
    border: 0.5px solid #d9dbde;
    border-radius: 8px;
  }
`;

const Badge = styled.div<StyledCardProps>`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  padding: 4px 8px;
  gap: 8px;
  position: absolute;
  height: 27px;
  left: 0px;
  right: 0px;
  top: 0px;
  background: ${(props) => (props.isActive ? "#435DFF" : "#FFD42B")};
  z-index: 2;
  box-sizing: border-box;
  transition: background-color 0.2s ease;

  ${CardContainer}:hover & {
    background: #435dff;
  }

  @media (max-width: 440px) {
    padding: 2px 4.5px;
    gap: 4.5px;
    height: 15px;
    border-radius: 4.5px 4.5px 0px 0px;
  }
`;

const BadgeTitle = styled.span<StyledCardProps>`
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 19px;
  display: flex;
  align-items: center;
  color: ${(props) => (props.isActive ? "#FFFFFF" : "#202123")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.2s ease;

  ${CardContainer}:hover & {
    color: #ffffff;
  }

  @media (max-width: 440px) {
    width: 91px;
    height: 11px;
    font-size: 7px;
    line-height: 11px;
    display: flex;
    align-items: flex-end;
  }
`;

const ImageArea = styled.div`
  width: 100%;
  height: 136.71px;
  position: relative;
  align-self: stretch;
  flex: none;
  z-index: 0;
  background: #d9d9d9;
  overflow: hidden;

  @media (max-width: 440px) {
    width: 100px;
    height: 80px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ProductInfo = styled.div<StyledCardProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  width: 100%;
  min-height: 160px;
  height: auto;
  background: ${(props) => (props.isActive ? "#283899" : "#435DFF")};
  box-sizing: border-box;
  align-self: stretch;
  flex: 1;
  z-index: 1;
  transition: background-color 0.2s ease;

  ${CardContainer}:hover & {
    background: #283899;
  }

  @media (max-width: 440px) {
    padding: 8px 9px;
    gap: 8px;
    width: 100px;
    height: 100px;
    min-height: auto;
  }
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 440px) {
    width: 16px;
    height: 16px;
  }
`;

const IconImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;

  @media (max-width: 440px) {
    width: 16px;
    height: 16px;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  min-height: 56px;
  height: auto;
  align-self: stretch;
  flex: 1;

  @media (max-width: 440px) {
    width: 82px;
    height: 32px;
    min-height: auto;
  }
`;

const Title = styled.h3`
  width: 100%;
  min-height: 56px;
  height: auto;
  font-style: normal;
  font-size: 20px;
  line-height: 26px;
  color: #ffd42b;
  margin: 0;
  padding: 0;
  align-self: stretch;
  flex: 1;
  word-wrap: break-word;
  overflow: visible;
  white-space: pre-line;

  @media (max-width: 440px) {
    width: 82px;
    height: 32px;
    font-family: "JUST Sans";
    font-style: normal;
    font-weight: 600;
    font-size: 13px;
    line-height: 16px;
    min-height: auto;
  }
`;

const InsuranceSpan = styled.span`
  color: #ffffff;
`;

const CardWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 163px;
  flex: none;
  z-index: 4;

  @media (max-width: 440px) {
    max-width: 100px;
    height: 180px;
  }
`;

const Triangle = styled.div`
  box-sizing: border-box;
  position: absolute;
  width: 48px;
  height: 34px;
  top: calc(100% + 16px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  pointer-events: none;

  @media (max-width: 440px) {
    display: none;
  }
`;

export const ProductCategoryCard = ({
  title,
  badgeText = undefined,
  icon = undefined,
  image = undefined,
  onClick = undefined,
  selected = undefined,
}: ProductCategoryCardProps) => {
  const [internalClicked, setInternalClicked] = useState(false);

  const isActive = selected !== undefined ? selected : internalClicked;

  const handleClick = () => {
    if (selected === undefined) {
      setInternalClicked(!internalClicked);
    }
    if (onClick) {
      onClick();
    }
  };

  const formatBadgeText = (badge?: string) => {
    if (!badge) return "";
    if (badge.length > 23) {
      return `${badge.substring(0, 23)}...`;
    }
    return badge;
  };

  const renderTitle = (text?: string) => {
    if (!text) return null;

    const cleanText = text.replace(/\n/g, " ");

    const match = cleanText.match(/(.*)\b(insurance)\b(.*)/i);
    if (match) {
      const before = match[1].trim();
      const insuranceWord = match[2];
      const after = match[3].trim();

      const mainText = [before, after].filter(Boolean).join(" ");

      return (
        <Title>
          {mainText}
          <br />
          <InsuranceSpan>{insuranceWord}</InsuranceSpan>
        </Title>
      );
    }

    const lines = text.split("\n");
    if (lines.length > 1) {
      return (
        <Title>
          <InsuranceSpan>{lines[0]}</InsuranceSpan>
          <br />
          {lines.slice(1).join(" ")}
        </Title>
      );
    }

    return <Title>{text}</Title>;
  };

  return (
    <CardWrapper>
      <CardContainer isActive={isActive} onClick={handleClick}>
        {badgeText && (
          <Badge isActive={isActive}>
            <BadgeTitle isActive={isActive}>
              {formatBadgeText(badgeText)}
            </BadgeTitle>
          </Badge>
        )}
        <ImageArea>{image && <CardImage src={image} alt={title} />}</ImageArea>
        <ProductInfo isActive={isActive}>
          <IconWrapper>{icon && <IconImage src={icon} alt="" />}</IconWrapper>
          <TextBlock>{renderTitle(title)}</TextBlock>
        </ProductInfo>
      </CardContainer>
      {isActive && (
        <Triangle>
          <svg
            width="48"
            height="34"
            viewBox="0 0 48 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="0" y="23" width="48" height="11" fill="#ECEFFF" />
            <path
              d="M0 24L24 1L48 24"
              fill="#ECEFFF"
              stroke="#D9DBDE"
              strokeWidth="1"
            />
          </svg>
        </Triangle>
      )}
    </CardWrapper>
  );
};

export default ProductCategoryCard;
